import type { Timestamp } from 'firebase/firestore'

export interface ViewerFieldConfig {
  fieldId: string
  visible: boolean
  editable: boolean
}

export interface ViewerDocumentTypeConfig {
  typeId: string
  visible: boolean
}

export interface ViewerSectionConfig {
  visible: boolean
  fields?: ViewerFieldConfig[]
}

export interface ViewerParentSectionConfig<TSub extends string> {
  visible: boolean
  subSections: Record<TSub, ViewerSectionConfig>
}

/** Módulos que tienen configuración de solo vista propia. */
export type ViewerModuleId = 'shipments' | 'purchaseOrders'

/** Permisos que cada módulo concede al viewer, al margen de los campos. */
interface ViewerModulePermissions {
  /** Si está en false el viewer no accede al módulo. */
  enabled: boolean
  /** Tipos de documento visibles. Vacío = todos. */
  documentTypes: ViewerDocumentTypeConfig[]
  canUploadFiles: boolean
  canExportData: boolean
  canComment: boolean
}

// === Embarques ===

export type ShipmentsMainInfoSection =
  | 'references' | 'transportDocs' | 'transportConfig' | 'incoterm' | 'carriers'

export type ShipmentsMilestonesSection =
  | 'requestedDates' | 'estimatedTimes' | 'actualEvents' | 'travelInfo' | 'lastMile'

export interface ViewerShipmentsTabs {
  timeline: boolean
  containers: boolean
  packages: boolean
  files: boolean
  products: boolean
  parties: boolean
  comments: boolean
  charges: boolean
  environmental: boolean
}

export interface ViewerShipmentsConfig extends ViewerModulePermissions {
  sections: {
    summary: ViewerSectionConfig
    mainInfo: ViewerParentSectionConfig<ShipmentsMainInfoSection>
    milestones: ViewerParentSectionConfig<ShipmentsMilestonesSection>
    additionalInfo: ViewerSectionConfig
  }
  tabs: ViewerShipmentsTabs
  /**
   * Satlock limitado (KILA-260 Parte B): si está encendido, el servidor solo entrega al viewer y a los
   * enlaces públicos el municipio, el departamento y los reportes enviados por correo. Apagado = KILA-371.
   */
  satlockLimitedTracking: boolean
}

// === Órdenes de compra ===

/** Coinciden con los valores de FieldSection que consume getSectionFields('purchaseOrders', X). */
export type PurchaseOrdersSection =
  | 'general' | 'dates' | 'commercial' | 'logistics' | 'items' | 'financialDetail' | 'inspection'

export const PURCHASE_ORDERS_SECTIONS: PurchaseOrdersSection[] = [
  'general', 'dates', 'commercial', 'logistics', 'items', 'financialDetail', 'inspection'
]

export interface ViewerPurchaseOrdersTabs {
  overview: boolean
  financialDetail: boolean
  inspection: boolean
  items: boolean
  timeline: boolean
  documents: boolean
  comments: boolean
  alerts: boolean
}

export interface ViewerPurchaseOrdersConfig extends ViewerModulePermissions {
  sections: Record<PurchaseOrdersSection, ViewerSectionConfig>
  /** Panel que muestra datos del embarque asociado dentro de la OC. */
  crossModule: ViewerSectionConfig
  tabs: ViewerPurchaseOrdersTabs
}

export interface ViewerViewConfig {
  modules: {
    shipments: ViewerShipmentsConfig
    purchaseOrders: ViewerPurchaseOrdersConfig
  }
  updatedAt?: Timestamp
  updatedBy?: string
}

/**
 * Formato anterior del documento: la configuración de embarques vivía en la raíz.
 * Se sigue leyendo para las compañías que aún no han vuelto a guardar.
 */
export interface LegacyViewerViewConfig {
  sections?: unknown
  tabs?: unknown
  documentTypes?: unknown
  canUploadFiles?: unknown
  canExportData?: unknown
}

/**
 * Embarques: todo visible, nada editable, sin permisos de escritura salvo
 * comentar, que hoy está abierto para el viewer.
 */
export const DEFAULT_VIEWER_SHIPMENTS_CONFIG: ViewerShipmentsConfig = {
  enabled: true,
  sections: {
    summary: { visible: true },
    mainInfo: {
      visible: true,
      subSections: {
        references: { visible: true },
        transportDocs: { visible: true },
        transportConfig: { visible: true },
        incoterm: { visible: true },
        carriers: { visible: true },
      }
    },
    milestones: {
      visible: true,
      subSections: {
        requestedDates: { visible: true },
        estimatedTimes: { visible: true },
        actualEvents: { visible: true },
        travelInfo: { visible: true },
        lastMile: { visible: true },
      }
    },
    additionalInfo: { visible: true },
  },
  tabs: {
    timeline: true,
    containers: true,
    packages: true,
    files: true,
    products: true,
    parties: true,
    comments: true,
    charges: true,
    environmental: true,
  },
  satlockLimitedTracking: false,
  documentTypes: [],
  canUploadFiles: false,
  canExportData: false,
  canComment: true,
}

/**
 * Órdenes de compra: `enabled` arranca en false porque hoy el viewer tiene el
 * módulo bloqueado por completo; abrirlo es una decisión del administrador.
 * `crossModule` también, porque expone datos del embarque dentro de la OC.
 */
export const DEFAULT_VIEWER_PURCHASE_ORDERS_CONFIG: ViewerPurchaseOrdersConfig = {
  enabled: false,
  sections: {
    general: { visible: true },
    dates: { visible: true },
    commercial: { visible: true },
    logistics: { visible: true },
    items: { visible: true },
    financialDetail: { visible: true },
    inspection: { visible: true },
  },
  crossModule: { visible: false },
  tabs: {
    overview: true,
    financialDetail: true,
    inspection: true,
    items: true,
    timeline: true,
    documents: true,
    comments: true,
    alerts: true,
  },
  documentTypes: [],
  canUploadFiles: false,
  canExportData: false,
  canComment: true,
}

export const DEFAULT_VIEWER_VIEW_CONFIG: ViewerViewConfig = {
  modules: {
    shipments: DEFAULT_VIEWER_SHIPMENTS_CONFIG,
    purchaseOrders: DEFAULT_VIEWER_PURCHASE_ORDERS_CONFIG,
  }
}

// === Normalización ===

type RawRecord = Record<string, any>

const asRecord = (value: unknown): RawRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as RawRecord) : {}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value))

const asBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback

const normalizeFields = (raw: unknown): ViewerFieldConfig[] | undefined => {
  if (!Array.isArray(raw)) return undefined
  return raw
    .filter((field) => asRecord(field).fieldId !== undefined)
    .map((field) => ({
      fieldId: String(field.fieldId),
      visible: asBoolean(field.visible, true),
      editable: asBoolean(field.editable, false),
    }))
}

const normalizeDocumentTypes = (raw: unknown, fallback: ViewerDocumentTypeConfig[]): ViewerDocumentTypeConfig[] => {
  if (!Array.isArray(raw)) return clone(fallback)
  return raw
    .filter((docType) => asRecord(docType).typeId !== undefined)
    .map((docType) => ({ typeId: String(docType.typeId), visible: asBoolean(docType.visible, true) }))
}

/**
 * Merge profundo de una sección. El merge superficial que hacía el loader
 * dejaba secciones a medias cuando el documento guardado no traía todas las
 * claves, y `parent.visible` reventaba dentro de un computed.
 */
const mergeSection = (base: ViewerSectionConfig, raw: unknown): ViewerSectionConfig => {
  const source = asRecord(raw)
  const merged: ViewerSectionConfig = { visible: asBoolean(source.visible, base.visible) }
  const fields = normalizeFields(source.fields) ?? base.fields
  if (fields) merged.fields = clone(fields)
  return merged
}

const mergeParentSection = <TSub extends string>(
  base: ViewerParentSectionConfig<TSub>,
  raw: unknown
): ViewerParentSectionConfig<TSub> => {
  const source = asRecord(raw)
  const subSource = asRecord(source.subSections)
  const subSections = {} as Record<TSub, ViewerSectionConfig>
  for (const key of Object.keys(base.subSections) as TSub[]) {
    subSections[key] = mergeSection(base.subSections[key], subSource[key])
  }
  return { visible: asBoolean(source.visible, base.visible), subSections }
}

const mergeTabs = <T extends object>(base: T, raw: unknown): T => {
  const source = asRecord(raw)
  const merged = { ...base }
  for (const key of Object.keys(base) as (keyof T & string)[]) {
    merged[key] = asBoolean(source[key], base[key] as boolean) as T[keyof T & string]
  }
  return merged
}

const mergePermissions = (base: ViewerModulePermissions, source: RawRecord): ViewerModulePermissions => ({
  enabled: asBoolean(source.enabled, base.enabled),
  documentTypes: normalizeDocumentTypes(source.documentTypes, base.documentTypes),
  canUploadFiles: asBoolean(source.canUploadFiles, base.canUploadFiles),
  canExportData: asBoolean(source.canExportData, base.canExportData),
  canComment: asBoolean(source.canComment, base.canComment),
})

const normalizeShipments = (raw: unknown): ViewerShipmentsConfig => {
  const base = clone(DEFAULT_VIEWER_SHIPMENTS_CONFIG)
  const source = asRecord(raw)
  const sections = asRecord(source.sections)
  return {
    ...mergePermissions(base, source),
    sections: {
      summary: mergeSection(base.sections.summary, sections.summary),
      mainInfo: mergeParentSection(base.sections.mainInfo, sections.mainInfo),
      milestones: mergeParentSection(base.sections.milestones, sections.milestones),
      additionalInfo: mergeSection(base.sections.additionalInfo, sections.additionalInfo),
    },
    tabs: mergeTabs(base.tabs, source.tabs),
    satlockLimitedTracking: asBoolean(source.satlockLimitedTracking, base.satlockLimitedTracking),
  }
}

const normalizePurchaseOrders = (raw: unknown): ViewerPurchaseOrdersConfig => {
  const base = clone(DEFAULT_VIEWER_PURCHASE_ORDERS_CONFIG)
  const source = asRecord(raw)
  const sections = asRecord(source.sections)
  const merged = {} as Record<PurchaseOrdersSection, ViewerSectionConfig>
  for (const key of PURCHASE_ORDERS_SECTIONS) {
    merged[key] = mergeSection(base.sections[key], sections[key])
  }
  return {
    ...mergePermissions(base, source),
    sections: merged,
    crossModule: mergeSection(base.crossModule, source.crossModule),
    tabs: mergeTabs(base.tabs, source.tabs),
  }
}

/**
 * Lleva cualquier versión del documento a la forma por módulo. Un documento en
 * el formato anterior tiene la configuración de embarques en la raíz, así que
 * se toma de ahí; los módulos ausentes caen a sus valores por defecto.
 */
export function normalizeViewerViewConfig(raw: unknown): ViewerViewConfig {
  const source = asRecord(raw)
  const modules = asRecord(source.modules)

  const shipmentsSource = modules.shipments ?? {
    sections: (source as LegacyViewerViewConfig).sections,
    tabs: (source as LegacyViewerViewConfig).tabs,
    documentTypes: (source as LegacyViewerViewConfig).documentTypes,
    canUploadFiles: (source as LegacyViewerViewConfig).canUploadFiles,
    canExportData: (source as LegacyViewerViewConfig).canExportData,
  }

  return {
    modules: {
      shipments: normalizeShipments(shipmentsSource),
      purchaseOrders: normalizePurchaseOrders(modules.purchaseOrders),
    }
  }
}

/**
 * Réplica de la configuración de embarques en las claves de raíz, para que un
 * rollback del frontend no pierda lo ya configurado. Se retira en la release
 * siguiente a la migración.
 */
export function legacyRootPayload(config: ViewerViewConfig): Required<LegacyViewerViewConfig> {
  const { sections, tabs, documentTypes, canUploadFiles, canExportData } = config.modules.shipments
  return { sections, tabs, documentTypes, canUploadFiles, canExportData }
}
